import React, { useState, useEffect } from 'react'
import { listEmailTemplates, deleteEmailTemplate } from '../api/emailSettingsApi'
import EmailTemplateEditorModal from './EmailTemplateEditorModal'
import { appConfirm } from '../../../components/AppConfirmModal'
import { appToast } from '../../../components/AppToast'

const CATEGORIES = ['ALL', 'APPROVAL', 'HR', 'PROJECT', 'GENERAL']

export default function EmailTemplatesTab() {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState('ALL')
  const [searchQuery, setSearchQuery] = useState('')
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [feedback, setFeedback] = useState(null)

  useEffect(() => {
    loadTemplates()
  }, [selectedCategory])

  async function loadTemplates() {
    setLoading(true)
    setFeedback(null)
    try {
      const data = await listEmailTemplates(selectedCategory)
      setTemplates(data)
    } catch (err) {
      setFeedback({
        type: 'error',
        message: 'Failed to load templates: ' + (err?.response?.data?.detail || err.message),
      })
    } finally {
      setLoading(false)
    }
  }

  function handleCreateNew() {
    setEditingTemplate(null)
    setIsEditorOpen(true)
  }

  function handleEdit(t) {
    setEditingTemplate(t)
    setIsEditorOpen(true)
  }

  async function handleDelete(t) {
    const isSystem = Boolean(t.isSystemTemplate ?? t.is_system_template)
    if (isSystem) {
      appToast.warning('System templates are protected and cannot be deleted.')
      return
    }

    const confirmed = await appConfirm({
      title: 'Delete Email Template',
      message: `Are you sure you want to delete custom template "${t.templateName || t.template_name}"?`,
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!confirmed) {
      return
    }

    try {
      await deleteEmailTemplate(t.id)
      setFeedback({ type: 'success', message: 'Template deleted successfully.' })
      loadTemplates()
    } catch (err) {
      setFeedback({
        type: 'error',
        message: 'Failed to delete template: ' + (err?.response?.data?.detail || err.message),
      })
    }
  }

  const filtered = templates.filter((t) => {
    if (!searchQuery.trim()) return true
    const q = searchQuery.toLowerCase()
    const name = (t.templateName || t.template_name || '').toLowerCase()
    const code = (t.templateCode || t.template_code || '').toLowerCase()
    const subj = (t.subject || '').toLowerCase()
    return name.includes(q) || code.includes(q) || subj.includes(q)
  })

  return (
    <div className="flex flex-col gap-6">
      {/* ── Top Canopy: Header & Action ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/15 pb-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">
            SYSTEM &amp; COMMUNICATIONS
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Email Templates Library &amp; WYSIWYG Editor
          </h1>
          <p className="mt-0.5 text-xs text-on-surface-variant">
            Manage system templates and build customized HTML email layouts with dynamic tags.
          </p>
        </div>

        <button
          type="button"
          onClick={handleCreateNew}
          className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-xs font-bold text-on-primary shadow-xs transition hover:brightness-110 active:scale-[0.98] self-start sm:self-auto"
        >
          <span className="material-symbols-outlined text-sm">add</span>
          Create Custom Template
        </button>
      </section>

      {/* ── Main Elevated Card: Filter Bar & Table ── */}
      <div className="rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs">

      {feedback && (
        <div
          className={`flex items-center gap-2.5 p-3.5 mb-5 rounded-2xl text-xs font-semibold ${
            feedback.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-rose-50 text-rose-800 border border-rose-200'
          }`}
        >
          <span className="material-symbols-outlined text-base">
            {feedback.type === 'success' ? 'check_circle' : 'error'}
          </span>
          <span>{feedback.message}</span>
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-1.5">
          {CATEGORIES.map((cat) => {
            const isActive = selectedCategory === cat
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`rounded-xl px-3 py-1.5 text-xs font-bold transition ${
                  isActive
                    ? 'bg-primary text-on-primary shadow-xs'
                    : 'border border-outline-variant/30 bg-surface-container-low text-on-surface hover:bg-surface-container'
                }`}
              >
                {cat}
              </button>
            )
          })}
        </div>

        <div className="relative min-w-[280px]">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-sm text-on-surface-variant/60 pointer-events-none">
            search
          </span>
          <input
            type="text"
            className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low pl-9 pr-3.5 py-1.5 text-xs font-semibold text-on-surface focus:outline-none focus:border-primary placeholder:text-on-surface-variant/40"
            placeholder="Search templates by code, name, subject..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Templates Table / Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center p-14 rounded-2xl border border-outline-variant/20 bg-surface-container-low">
          <span className="material-symbols-outlined text-4xl text-primary animate-spin">sync</span>
          <p className="mt-3 text-xs text-on-surface-variant font-medium">Loading templates...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 rounded-2xl border border-dashed border-outline-variant/40 bg-surface-container-low text-center">
          <span className="material-symbols-outlined text-4xl text-on-surface-variant/50">
            mark_email_unread
          </span>
          <p className="mt-2 text-xs font-bold text-on-surface">No email templates found.</p>
          <p className="text-[11px] text-on-surface-variant mt-0.5">
            {searchQuery ? 'Try clearing your search query.' : 'Get started by creating your first custom template.'}
          </p>
          <button
            type="button"
            className="mt-4 inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-high transition"
            onClick={handleCreateNew}
          >
            <span className="material-symbols-outlined text-sm">add</span>
            Create your first template
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-outline-variant/20 bg-surface-container-lowest">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-surface-container-low/75 text-[11px] font-bold uppercase tracking-wider text-on-surface-variant border-b border-outline-variant/20">
                <th className="px-4 py-3 font-extrabold w-[22%]">Template Code</th>
                <th className="px-4 py-3 font-extrabold w-[22%]">Name</th>
                <th className="px-4 py-3 font-extrabold w-[12%]">Category</th>
                <th className="px-4 py-3 font-extrabold w-[26%]">Subject Line</th>
                <th className="px-4 py-3 font-extrabold w-[10%]">Type</th>
                <th className="px-4 py-3 font-extrabold w-[8%] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15">
              {filtered.map((t) => {
                const isSystem = Boolean(t.isSystemTemplate ?? t.is_system_template)
                return (
                  <tr key={t.id || t.templateCode} className="hover:bg-surface-container-low/50 transition">
                    <td className="px-4 py-3 font-mono font-bold text-primary">
                      {t.templateCode || t.template_code}
                    </td>
                    <td className="px-4 py-3 font-bold text-on-surface">
                      {t.templateName || t.template_name}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-surface-container-high text-on-surface-variant border border-outline-variant/20">
                        {t.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-on-surface-variant">
                      {t.subject || '—'}
                    </td>
                    <td className="px-4 py-3">
                      {isSystem ? (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-extrabold bg-blue-50 text-blue-700 border border-blue-200"
                          title="Built-in starter template"
                        >
                          System
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-extrabold bg-violet-50 text-violet-700 border border-violet-200"
                          title="Tenant custom template"
                        >
                          Custom
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5 justify-end">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center h-7 w-7 rounded-lg border border-outline-variant/30 bg-surface-container-low text-on-surface hover:bg-surface-container-high transition"
                          onClick={() => handleEdit(t)}
                          title="Edit Template"
                        >
                          <span className="material-symbols-outlined text-[15px]">edit</span>
                        </button>
                        {!isSystem && (
                          <button
                            type="button"
                            className="inline-flex items-center justify-center h-7 w-7 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 transition"
                            onClick={() => handleDelete(t)}
                            title="Delete Custom Template"
                          >
                            <span className="material-symbols-outlined text-[15px]">delete</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Editor Modal */}
      {isEditorOpen && (
        <EmailTemplateEditorModal
          template={editingTemplate}
          isOpen={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          onSaved={() => {
            loadTemplates()
            setFeedback({ type: 'success', message: 'Template saved successfully!' })
          }}
        />
      )}
      </div>
    </div>
  )
}

